import { cn } from '@/shared/utilities';
import { OpenInNew } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import React from 'react';
import type { LinkAttachment } from '../../models/attachment';

interface ComposeLinkProps {
  attachment: LinkAttachment;
  onRemove?: (attachmentId: string) => void;
  mode?: 'compact' | 'comfortable';
  isOwner: boolean;
}

export const ComposeLink: React.FC<ComposeLinkProps> = React.memo(
  ({ attachment, onRemove, mode = 'comfortable', isOwner }) => {
    const isCompact = mode === 'compact';

    // Extract domain from URL for display
    const getDomainFromUrl = (url: string): string => {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
      } catch {
        return 'Unknown site';
      }
    };

    const handleLinkClick = () => {
      if (attachment.url) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
      }
    };

    const domain = attachment.url ? getDomainFromUrl(attachment.url) : 'Unknown site';
    const displayTitle = attachment.filename || domain;
    const displayDescription = attachment.description;
    const thumbnailUrl = attachment.imageUrl;

    return (
      <div
        className={cn(
          'mex:group/mex:link mex:relative mex:mx-2 mex:mb-2 mex:cursor-pointer mex:overflow-hidden mex:rounded-lg mex:border mex:border-gray-200 mex:bg-gray-50 mex:transition-all mex:hover:bg-gray-100',
          'mex:dark:border-primary-900/20 mex:dark:bg-primary-900/10 mex:hover:dark:bg-primary-900/20',
          isCompact && 'mex:max-w-[320px]'
        )}
        onClick={handleLinkClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLinkClick();
          }
        }}
      >
        {/* Blue left border */}
        <div
          className={cn(
            'mex:absolute mex:top-0 mex:left-0 mex:h-full mex:w-1 mex:bg-blue-500 mex:dark:bg-primary-main',
            isOwner ? 'mex:left-0' : 'mex:right-0'
          )}
        />

        {/* Content container */}
        <div
          className={cn(
            'mex:flex mex:items-center mex:gap-3 mex:p-3',
            isOwner ? 'mex:flex-row' : 'mex:flex-row-reverse'
          )}
        >
          {/* Thumbnail */}
          <div className='mex:h-16 mex:w-16 mex:flex-shrink-0 mex:overflow-hidden mex:rounded mex:bg-gray-200 mex:dark:bg-primary-900/30'>
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={displayTitle}
                className='mex:h-full mex:w-full mex:object-cover'
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('mex:hidden');
                }}
              />
            ) : null}
            {/* Placeholder icon */}
            <div
              className={cn(
                'mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:bg-gray-200 mex:dark:bg-primary-900/30',
                thumbnailUrl ? 'mex:hidden' : 'mex:flex'
              )}
            >
              <svg
                className='mex:h-6 mex:w-6 mex:text-gray-400'
                fill='currentColor'
                viewBox='0 0 20 20'
                aria-hidden='true'
              >
                <path
                  fillRule='evenodd'
                  d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
          </div>
          {/* Text content */}
          <div className='mex:min-w-0 mex:flex-1 mex:pr-3'>
            {/* Domain */}
            <Typography
              variant='caption'
              className='mex:mb-1 mex:block mex:text-gray-600 mex:text-xs mex:dark:text-gray-400'
            >
              {domain}
            </Typography>

            {/* Title */}
            <Typography
              variant='body2'
              sx={{ fontWeight: 600 }}
              className='mex:mb-1 mex:overflow-hidden mex:text-gray-900 mex:dark:text-gray-100'
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
              title={displayTitle}
            >
              {displayTitle}
            </Typography>

            {/* Description */}
            {displayDescription && (
              <Typography
                variant='caption'
                className='mex:overflow-hidden mex:text-gray-600 mex:dark:text-gray-400'
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
                title={displayDescription}
              >
                {displayDescription}
              </Typography>
            )}
          </div>
        </div>

        {/* Hover overlay with open button */}
        <div className='mex:absolute mex:inset-0 mex:flex mex:items-center mex:justify-center mex:bg-black/20 mex:bg-opacity-0 mex:opacity-0 mex:transition-all mex:duration-200 mex:group-hover/mex:link:bg-opacity-5 mex:group-hover/mex:link:opacity-100'>
          <IconButton
            size='medium'
            onClick={(e) => {
              e.stopPropagation();
              handleLinkClick();
            }}
            className='mex:bg-white mex:shadow-lg mex:hover:bg-gray-50 mex:dark:bg-gray-800 mex:dark:hover:bg-gray-700'
          >
            <OpenInNew className='mex:!size-4 mex:text-gray-700 mex:dark:text-gray-200' />
          </IconButton>
        </div>

        {/* Remove Button (if onRemove is provided) */}
        {onRemove && (
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onRemove(attachment.id || '');
            }}
            className='mex:absolute mex:top-2 mex:right-2 mex:bg-white mex:text-gray-400 mex:shadow-sm mex:transition-colors mex:hover:bg-red-50 mex:hover:text-red-500 mex:dark:bg-gray-800 mex:dark:hover:bg-red-900/20'
            sx={{
              '&:hover': {
                backgroundColor: 'error.lighter'
              }
            }}
          >
            <span className='mex:!size-4'>×</span>
          </IconButton>
        )}
      </div>
    );
  }
);
